import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Order, TicketPriority, TicketQueue } from '@runninglime/shared';
import { TICKET_QUEUE_LABELS } from '@runninglime/shared';
import { apiGet, apiPost } from '../../lib/api.ts';

const QUEUES: TicketQueue[] = ['general', 'billing', 'shipping', 'product-issue', 'returns', 'technical'];

export default function NewTicket() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [subject, setSubject] = useState('');
  const [orderId, setOrderId] = useState('');
  const [priority, setPriority] = useState<TicketPriority>('normal');
  const [queue, setQueue] = useState<TicketQueue>('general');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      const res = await apiGet<{ items: Order[]; total: number }>('/orders');
      if (res.success && res.data) setOrders(res.data.items);
    };
    load();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!subject.trim() || !message.trim()) {
      setError('Subject and message are required');
      return;
    }
    setSubmitting(true);
    try {
      const res = await apiPost('/tickets', {
        subject: subject.trim(),
        message: message.trim(),
        orderId: orderId || undefined,
        priority,
        queue,
      });
      if (res.success) {
        navigate('/support');
      } else {
        setError(res.error || 'Failed to create ticket');
      }
    } catch {
      setError('Something went wrong');
    }
    setSubmitting(false);
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-in">
      <h1 className="text-3xl font-bold text-dark mb-2">New Support Ticket</h1>
      <p className="text-gray-500 mb-8">Describe your issue and we'll get back to you</p>

      <form onSubmit={handleSubmit} className="glass rounded-2xl p-6 space-y-5 animate-slide-up">
        {error && (
          <div className="bg-red-50 text-red-600 px-4 py-3 rounded-xl text-sm">{error}</div>
        )}

        <div>
          <label htmlFor="subject" className="block text-sm font-medium text-dark mb-1">Subject *</label>
          <input
            id="subject"
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Brief description of your issue"
            className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-lime/50 focus:border-lime outline-none transition"
          />
        </div>

        <div>
          <label htmlFor="order" className="block text-sm font-medium text-dark mb-1">Related Order (optional)</label>
          <select
            id="order"
            value={orderId}
            onChange={(e) => setOrderId(e.target.value)}
            className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-lime/50 focus:border-lime outline-none transition bg-white"
          >
            <option value="">No order</option>
            {orders.map((o) => (
              <option key={o.id} value={o.id}>
                #{o.id.slice(0, 8)} — €{((o.total || 0) / 100).toFixed(2)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="queue" className="block text-sm font-medium text-dark mb-1">Category</label>
          <select
            id="queue"
            value={queue}
            onChange={(e) => setQueue(e.target.value as TicketQueue)}
            className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-lime/50 focus:border-lime outline-none transition bg-white"
          >
            {QUEUES.map((q) => (
              <option key={q} value={q}>
                {TICKET_QUEUE_LABELS[q]}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-dark mb-1">Priority</label>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setPriority('normal')}
              className={`flex-1 py-2.5 rounded-xl text-sm font-medium border transition ${
                priority === 'normal'
                  ? 'bg-blue-50 text-blue-700 border-blue-200'
                  : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
              }`}
            >
              🔵 Normal
            </button>
            <button
              type="button"
              onClick={() => setPriority('urgent')}
              className={`flex-1 py-2.5 rounded-xl text-sm font-medium border transition ${
                priority === 'urgent'
                  ? 'bg-red-50 text-red-700 border-red-200'
                  : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
              }`}
            >
              🔴 Urgent
            </button>
          </div>
        </div>

        <div>
          <label htmlFor="message" className="block text-sm font-medium text-dark mb-1">Message *</label>
          <textarea
            id="message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={6}
            placeholder="Describe the issue in detail..."
            className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-lime/50 focus:border-lime outline-none transition resize-none"
          />
        </div>

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={() => navigate('/support')}
            className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-600 font-medium hover:bg-gray-50 transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="flex-1 btn-gradient py-3 rounded-xl font-semibold disabled:opacity-50 active:scale-[0.98] transition-transform"
          >
            {submitting ? 'Submitting...' : 'Submit Ticket'}
          </button>
        </div>
      </form>
    </div>
  );
}
